import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  BarChart3,
  Layers,
  Loader2,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Button from '../../shared/components/ui/Button';
import Modal from '../../shared/components/ui/Modal';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import { useSmartBack } from '../../shared/navigation';
import {
  getAllCompanies,
  getFbMetaCampaigns,
  syncFbMetaCampaigns,
  getMarketingCampaignSummary,
  getMarketingCampaignBreakdown
} from '../services/api/implementation/impl';

const STATUS_STYLES = {
  ACTIVE: 'text-primary border-primary/50 bg-primary/10',
  PAUSED: 'text-amber-700 border-amber-300/80 bg-amber-50'
};

const formatInr = (amount) =>
  `₹${Number(amount).toLocaleString('en-IN', {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  })}`;

const RANGE_OPTIONS = [
  { key: 'last_7d', label: 'Last 7 days' },
  { key: 'all', label: 'All the time' },
  { key: 'custom', label: 'Custom range' }
];

const MAX_CUSTOM_RANGE_DAYS = 366;

const normalizeCampaigns = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.campaigns)) return data.campaigns;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const formatShortDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return String(value);
  }
};

const inclusiveDaySpan = (from, to) => {
  if (!from || !to) return null;
  const start = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
};

const formatRangeLabel = (from, to) => {
  if (!from || !to) return '';
  return `${formatShortDate(from)} – ${formatShortDate(to)}`;
};

const formatSyncedAt = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const weekdayLabel = (dateStr) => {
  try {
    return new Date(dateStr).toLocaleDateString(undefined, { weekday: 'short' });
  } catch {
    return dateStr;
  }
};

const formatPct = (value, { signed = true, suffix = '%' } = {}) => {
  if (value == null || Number.isNaN(Number(value))) return null;
  const n = Number(value);
  const abs = Math.abs(n);
  const body = Number.isInteger(abs) ? String(abs) : abs.toFixed(1);
  if (!signed) return `${body}${suffix}`;
  if (n > 0) return `+${body}${suffix}`;
  if (n < 0) return `-${body}${suffix}`;
  return `${body}${suffix}`;
};

const mapWeeklyBars = (bars = []) => {
  if (!Array.isArray(bars) || !bars.length) return [];
  const max = Math.max(...bars.map((b) => Number(b.count) || 0), 0);
  return bars.map((b) => ({
    day: weekdayLabel(b.date),
    count: Number(b.count) || 0,
    highlight: max > 0 && Number(b.count) === max,
    title: `${b.date}: ${b.count} preleads`
  }));
};

const kpiSentiment = (pct, { invert = false } = {}) => {
  if (pct == null || Number.isNaN(Number(pct))) return 'neutral';
  const n = Number(pct);
  if (n === 0) return 'neutral';
  const positive = invert ? n < 0 : n > 0;
  return positive ? 'positive' : 'negative';
};

const formatKpiValue = (key, value) => {
  if (value == null) return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  if (key === 'cpc' || key === 'cpl') return formatInr(n);
  if (key === 'ctr' || key === 'conversion_rate') {
    return `${Number.isInteger(n) ? n : n.toFixed(2)}%`;
  }
  return n.toLocaleString('en-IN');
};

const buildBreakdownKpis = (kpis = {}, range) => {
  const showTrend = range === 'last_7d' || range === 'custom';
  const trendLabelText =
    range === 'custom' ? 'vs previous period' : range === 'last_7d' ? 'vs previous 7D' : '';
  const defs = [
    { key: 'impressions', label: 'Impressions' },
    { key: 'clicks', label: 'Clicks' },
    { key: 'ctr', label: 'CTR' },
    { key: 'cpc', label: 'CPC', invert: true },
    { key: 'leads', label: 'Preleads' },
    { key: 'cpl', label: 'Cost per prelead', invert: true },
    { key: 'conversion_rate', label: 'Conversion Rate' },
    { key: 'quality_leads', label: 'Quality Preleads', special: true }
  ];

  return defs.map((def) => {
    const node = kpis[def.key];
    if (def.special) {
      const pctOf = node?.pct_of_leads;
      return {
        label: def.label,
        value: node?.value != null ? Number(node.value).toLocaleString('en-IN') : '—',
        trend:
          pctOf != null
            ? `${Number.isInteger(Number(pctOf)) ? pctOf : Number(pctOf).toFixed(1)}% of total preleads`
            : null,
        trendLabel: '',
        sentiment: 'neutral'
      };
    }
    const vs = node?.vs_previous_7d_pct;
    return {
      label: def.label,
      value: formatKpiValue(def.key, node?.value),
      trend: showTrend ? formatPct(vs) : null,
      trendLabel: showTrend && vs != null ? trendLabelText : '',
      sentiment: showTrend ? kpiSentiment(vs, { invert: def.invert }) : 'neutral'
    };
  });
};

function WeeklyMiniChart({ weeklyLeads }) {
  if (!weeklyLeads?.length) {
    return (
      <div className="mt-4 h-16 flex items-center justify-center text-xs text-gray-400">
        No weekly prelead data
      </div>
    );
  }

  const max = Math.max(...weeklyLeads.map((d) => d.count), 1);

  return (
    <div className="mt-4">
      <div className="flex items-end justify-between gap-1.5 h-16">
        {weeklyLeads.map((item, idx) => (
          <div key={`${item.day}-${idx}`} className="flex flex-1 flex-col items-center gap-1">
            <div
              className={`w-full rounded-t-md transition-colors ${
                item.highlight ? 'bg-primary' : 'bg-primary/25'
              }`}
              style={{ height: `${Math.max((item.count / max) * 100, 12)}%` }}
              title={item.title || `${item.day}: ${item.count} preleads`}
            />
            <span
              className={`text-[10px] font-medium ${
                item.highlight ? 'text-primary' : 'text-gray-400'
              }`}>
              {item.day}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function KpiTile({ kpi }) {
  const trendClass =
    kpi.sentiment === 'positive'
      ? 'text-emerald-600'
      : kpi.sentiment === 'negative'
        ? 'text-rose-600'
        : 'text-gray-500';

  return (
    <div className="rounded-xl border border-stone-200 bg-[#F8F9FA] px-4 py-3.5">
      <p className="text-[10px] font-semibold tracking-wide text-gray-400 uppercase">
        {kpi.label}
      </p>
      <p className="text-xl font-semibold text-gray-900 tabular-nums mt-1">{kpi.value}</p>
      {kpi.trend ? (
        <p className={`text-xs font-medium mt-1 ${trendClass}`}>
          {kpi.trend} {kpi.trendLabel}
        </p>
      ) : null}
    </div>
  );
}

function CampaignBreakdownModal({ campaignId, campaignName, onClose }) {
  const [mode, setMode] = useState('last_7d'); // last_7d | all | custom
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');
  const [appliedCustom, setAppliedCustom] = useState(null); // { from, to }
  const [localError, setLocalError] = useState('');

  const queryParams = useMemo(() => {
    if (mode === 'custom' && appliedCustom?.from && appliedCustom?.to) {
      return { from: appliedCustom.from, to: appliedCustom.to };
    }
    if (mode === 'all') return 'all';
    if (mode === 'last_7d') return 'last_7d';
    return null;
  }, [mode, appliedCustom]);

  const queryEnabled = Boolean(campaignId && queryParams);

  const {
    data: breakdown,
    isPending,
    isError,
    error,
    isFetching
  } = useQuery({
    queryKey: ['marketing-campaign-breakdown', campaignId, queryParams],
    queryFn: () => getMarketingCampaignBreakdown(campaignId, queryParams),
    enabled: queryEnabled,
    staleTime: 0
  });

  const effectiveRange = breakdown?.range || (mode === 'custom' ? 'custom' : mode);

  const kpis = useMemo(
    () => buildBreakdownKpis(breakdown?.kpis, effectiveRange),
    [breakdown, effectiveRange]
  );

  const dailyRows = Array.isArray(breakdown?.daily) ? breakdown.daily : [];
  const acquisition = breakdown?.customer_acquisition;

  const customRangeValid = useMemo(() => {
    if (!draftFrom || !draftTo) return false;
    const span = inclusiveDaySpan(draftFrom, draftTo);
    return span != null && span > 0 && span <= MAX_CUSTOM_RANGE_DAYS;
  }, [draftFrom, draftTo]);

  const handleSelectMode = (nextMode) => {
    setLocalError('');
    setMode(nextMode);
    if (nextMode !== 'custom') {
      setAppliedCustom(null);
    }
  };

  const handleApplyCustom = () => {
    setLocalError('');
    if (!draftFrom || !draftTo) {
      setLocalError('Select both From and To dates');
      return;
    }
    const span = inclusiveDaySpan(draftFrom, draftTo);
    if (span == null) {
      setLocalError('Invalid date format');
      return;
    }
    if (span < 1) {
      setLocalError('From must be on or before To');
      return;
    }
    if (span > MAX_CUSTOM_RANGE_DAYS) {
      setLocalError(`Date range cannot exceed ${MAX_CUSTOM_RANGE_DAYS} days`);
      return;
    }
    setAppliedCustom({ from: draftFrom, to: draftTo });
  };

  const activeCustomLabel =
    mode === 'custom' && appliedCustom
      ? formatRangeLabel(
          breakdown?.from || appliedCustom.from,
          breakdown?.to || appliedCustom.to
        )
      : '';

  const showEmptyCustom = mode === 'custom' && !appliedCustom;

  return (
    <Modal
      name={`${campaignName || breakdown?.name || 'Campaign'} — Full breakdown`}
      width={88}
      onClose={onClose}>
      <div className="space-y-6">
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 mt-1">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => handleSelectMode(opt.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                  mode === opt.key
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-600 border-stone-200 hover:border-primary/40'
                }`}>
                {opt.label}
              </button>
            ))}
          </div>

          {mode === 'custom' ? (
            <div className="flex flex-col gap-3 rounded-lg border border-stone-200 bg-gray-50 px-4 py-3">
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1 text-sm text-gray-700">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    From
                  </span>
                  <input
                    type="date"
                    className="p-2 border border-stone-300 rounded bg-white text-sm"
                    value={draftFrom}
                    max={draftTo || undefined}
                    onChange={(e) => {
                      setDraftFrom(e.target.value);
                      setLocalError('');
                    }}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-gray-700">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    To
                  </span>
                  <input
                    type="date"
                    className="p-2 border border-stone-300 rounded bg-white text-sm"
                    value={draftTo}
                    min={draftFrom || undefined}
                    onChange={(e) => {
                      setDraftTo(e.target.value);
                      setLocalError('');
                    }}
                  />
                </label>
                <Button
                  displayType="secondary"
                  onClick={handleApplyCustom}
                  disabled={!customRangeValid}>
                  Apply
                </Button>
              </div>
              {activeCustomLabel ? (
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{activeCustomLabel}</span>
                </p>
              ) : (
                <p className="text-xs text-gray-500">
                  Pick From and To (max {MAX_CUSTOM_RANGE_DAYS} days), then Apply.
                </p>
              )}
              {localError ? <p className="text-sm text-rose-600">{localError}</p> : null}
            </div>
          ) : null}
        </div>

        {showEmptyCustom ? (
          <p className="text-sm text-gray-500 py-8 text-center">
            Select a date range and click Apply to load the breakdown.
          </p>
        ) : isPending ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading breakdown…
          </div>
        ) : isError ? (
          <p className="text-sm text-rose-600 py-4">
            {error?.response?.data?.message || error?.message || 'Failed to load breakdown'}
          </p>
        ) : (
          <>
            {isFetching ? (
              <p className="text-xs text-gray-400 flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" />
                Updating…
              </p>
            ) : null}

            {breakdown?.mapping_incomplete ? (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  Link this campaign to a project to track preleads &amp; cost per prelead.
                </span>
              </div>
            ) : null}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {kpis.map((kpi) => (
                <KpiTile key={kpi.label} kpi={kpi} />
              ))}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Daily breakdown</h3>
              <div className="overflow-x-auto rounded-xl border border-stone-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-[#E9F5F6] text-left">
                    <tr>
                      {[
                        'Date',
                        'Impressions',
                        'Clicks',
                        'Preleads',
                        'CTR',
                        'CPC',
                        'Cost per prelead',
                        'Conv. Rate'
                      ].map((col) => (
                        <th
                          key={col}
                          className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-gray-600 whitespace-nowrap">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dailyRows.length ? (
                      dailyRows.map((row) => (
                        <tr key={row.date} className="border-t border-stone-100 even:bg-gray-50/60">
                          <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
                            {formatShortDate(row.date)}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-gray-600">
                            {Number(row.impressions ?? 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-gray-600">
                            {Number(row.clicks ?? 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-gray-600">
                            {Number(row.leads ?? 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-gray-600">
                            {row.ctr != null ? `${row.ctr}%` : '—'}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-gray-600">
                            {row.cpc != null ? formatInr(row.cpc) : '—'}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-gray-600">
                            {row.cpl != null ? formatInr(row.cpl) : '—'}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-gray-600">
                            {row.conversion_rate != null ? `${row.conversion_rate}%` : '—'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="px-4 py-6 text-center text-gray-500">
                          No daily rows for this range
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {acquisition ? (
              <div className="rounded-xl bg-primary px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-white">
                <div>
                  <p className="text-lg font-semibold">Customer Acquisition Rate</p>
                  <p className="text-sm text-white/85 mt-0.5">
                    Preleads converted to paying customers
                    {effectiveRange === 'last_7d'
                      ? ', previous 7D'
                      : effectiveRange === 'custom'
                        ? ', selected period'
                        : ''}
                  </p>
                  <p className="text-xs text-white/75 mt-1">
                    {acquisition.label ||
                      `${acquisition.closed_deals ?? 0} / ${acquisition.contactable_leads ?? 0} contactable preleads`}
                  </p>
                </div>
                <p className="text-3xl font-bold tabular-nums">
                  {acquisition.rate != null ? `${acquisition.rate}%` : '—'}
                </p>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  );
}

function CampaignCard({ campaign, onViewBreakdown, onLinkProject }) {
  const statusKey = campaign.status?.toUpperCase() === 'PAUSED' ? 'PAUSED' : 'ACTIVE';
  const leads =
    campaign.leads_generated != null ? Number(campaign.leads_generated).toLocaleString('en-IN') : '—';
  const weeklyLeads = mapWeeklyBars(campaign.weekly_lead_bars);
  const todaysLeads =
    campaign.todays_leads != null ? Number(campaign.todays_leads).toLocaleString('en-IN') : '—';
  const totalExpense =
    campaign.total_expense != null && !Number.isNaN(Number(campaign.total_expense))
      ? formatInr(campaign.total_expense)
      : '—';

  return (
    <article
      className="group bg-white rounded-[22px] flex flex-col h-full min-h-0 overflow-hidden transition-shadow hover:shadow-lg border-2 border-primary"
      style={{
        boxShadow:
          '0 10px 28px -12px rgba(42, 86, 94, 0.18), 0 4px 12px -6px rgba(42, 86, 94, 0.08)'
      }}>
      <div className="p-5 flex gap-3.5 shrink-0">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-gray-900 bg-primary/20">
          <Layers className="w-5 h-5" strokeWidth={2.25} />
        </div>
        <div className="flex-1 min-w-0 pt-0.5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-bold text-gray-900 text-base leading-tight truncate">
                {campaign.name || '—'}
              </h3>
              <p className="text-sm text-gray-500 mt-1 truncate">
                {String(campaign.objective_label || campaign.objective || '—')
                  .replace(/Lead gen/gi, 'Prelead gen')
                  .replace(/\bLeads\b/gi, 'Preleads')
                  .replace(/\bLead\b/gi, 'Prelead')}
              </p>
            </div>
            <span
              className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${STATUS_STYLES[statusKey] || STATUS_STYLES.ACTIVE}`}>
              {statusKey}
            </span>
          </div>
        </div>
      </div>

      <div className="px-5 pb-2 flex-1 min-h-0">
        {campaign.mapping_incomplete ? (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              Link this campaign to a project to track preleads &amp; cost per prelead.{' '}
              <button
                type="button"
                className="underline font-medium"
                onClick={onLinkProject}>
                Link project
              </button>
            </span>
          </div>
        ) : null}

        <p className="text-3xl font-bold text-gray-900 tabular-nums">
          {leads}{' '}
          <span className="text-base font-medium text-gray-500">preleads generated</span>
        </p>

        <WeeklyMiniChart weeklyLeads={weeklyLeads} />

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-stone-200 bg-[#F8F9FA] px-3 py-2.5">
            <p className="text-[10px] font-semibold tracking-wide text-gray-400 uppercase">
              Today&apos;s preleads
            </p>
            <p className="text-lg font-semibold text-primary tabular-nums mt-0.5">{todaysLeads}</p>
          </div>
          <div className="rounded-xl border border-stone-200 bg-[#F8F9FA] px-3 py-2.5">
            <p className="text-[10px] font-semibold tracking-wide text-gray-400 uppercase">
              Total expense
            </p>
            <p className="text-lg font-semibold text-gray-800 tabular-nums mt-0.5">{totalExpense}</p>
          </div>
        </div>

        <div className="mt-4 flex justify-between text-xs text-gray-500">
          <span>
            Created on{' '}
            <span className="font-medium text-gray-700">{formatShortDate(campaign.created_on)}</span>
          </span>
          <span>
            Updated on{' '}
            <span className="font-medium text-gray-700">{formatShortDate(campaign.updated_on)}</span>
          </span>
        </div>
      </div>

      <div className="mt-auto border-t border-gray-200/90 px-4 py-3.5 shrink-0 bg-white">
        <button
          type="button"
          onClick={onViewBreakdown}
          className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-primary text-white text-sm font-semibold shadow-sm transition-colors hover:bg-primary/90">
          <BarChart3 className="w-4 h-4" />
          View Breakdown
        </button>
      </div>
    </article>
  );
}

const FacebookCampaignsDashboard = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { collapsed } = useSidebar();
  const { onBack } = useSmartBack({ fallback: '/builder/marketing' });

  const initialCompanyId =
    localStorage.getItem('companyId') || Cookies.get('companyId') || Cookies.get('company_id') || '';
  const [companyId, setCompanyId] = useState(String(initialCompanyId || ''));
  const [statusFilter, setStatusFilter] = useState('all'); // all | ACTIVE | PAUSED
  const [breakdownCampaign, setBreakdownCampaign] = useState(null);

  const { data: companies = [] } = useQuery({
    queryKey: ['allCompanies'],
    queryFn: getAllCompanies
  });

  useEffect(() => {
    if (!companyId && companies.length) {
      const first = String(companies[0].id);
      setCompanyId(first);
      localStorage.setItem('companyId', first);
    }
  }, [companies, companyId]);

  useEffect(() => {
    mixpanel.track('Facebook Campaigns Dashboard Viewed', {
      user_id: userId,
      company_id: companyId || null,
      timestamp: new Date().toISOString()
    });
  }, [userId, companyId]);

  const {
    data: campaignsData,
    isPending: isCampaignsPending,
    isError: isCampaignsError,
    error: campaignsError,
    refetch: refetchCampaigns
  } = useQuery({
    queryKey: ['fb-meta-campaigns', companyId],
    queryFn: () => getFbMetaCampaigns(companyId),
    enabled: !!companyId,
    staleTime: 0
  });

  const metaCampaigns = useMemo(() => normalizeCampaigns(campaignsData), [campaignsData]);
  const campaignIds = useMemo(() => metaCampaigns.map((c) => c.id).filter(Boolean), [metaCampaigns]);

  const summaryQueries = useQueries({
    queries: campaignIds.map((id) => ({
      queryKey: ['marketing-campaign-summary', id],
      queryFn: () => getMarketingCampaignSummary(id),
      enabled: !!id,
      // App-wide staleTime is 100 min; live ad metrics must not sit on that cache.
      staleTime: 0
    }))
  });

  const hasAnySummary = summaryQueries.some((q) => q.data);
  const summariesInitialPending =
    campaignIds.length > 0 && !hasAnySummary && summaryQueries.some((q) => q.isPending);

  const campaigns = campaignIds.map((id, index) => {
    const meta = metaCampaigns.find((c) => String(c.id) === String(id)) || {};
    const summary = summaryQueries[index]?.data;
    if (summary) return summary;
    return {
      id: meta.id,
      name: meta.name,
      status: meta.status,
      objective: meta.objective,
      objective_label: meta.objective_label || meta.objective,
      form_id: meta.form_id,
      pre_lead_list_id: meta.pre_lead_list_id,
      project_id: meta.project_id,
      mapping_incomplete: !meta.pre_lead_list_id,
      synced_at: meta.synced_at,
      created_on: meta.created_at || meta.created_on,
      updated_on: meta.updated_at || meta.updated_on,
      _summaryError: summaryQueries[index]?.isError
    };
  });

  const lastSynced = useMemo(() => {
    // Sync/list response is authoritative for synced_at; summaries can lag behind it.
    const times = metaCampaigns
      .map((c) => c.synced_at)
      .filter(Boolean)
      .map((t) => new Date(t).getTime())
      .filter((t) => !Number.isNaN(t));
    if (!times.length) return '—';
    return formatSyncedAt(new Date(Math.max(...times)).toISOString());
  }, [metaCampaigns]);

  const filteredCampaigns = useMemo(() => {
    if (statusFilter === 'all') return campaigns;
    return campaigns.filter(
      (c) => String(c.status || '').toUpperCase() === statusFilter
    );
  }, [campaigns, statusFilter]);

  const { mutate: syncNow, isPending: isSyncing } = useMutation({
    mutationFn: () => syncFbMetaCampaigns({ company_id: Number(companyId) }),
    onSuccess: async (data) => {
      const list = normalizeCampaigns(data);
      queryClient.setQueryData(['fb-meta-campaigns', companyId], data?.campaigns ? data : list);

      const ids = list.map((c) => c.id).filter(Boolean);
      await Promise.all(
        ids.map((id) =>
          queryClient.fetchQuery({
            queryKey: ['marketing-campaign-summary', id],
            queryFn: () => getMarketingCampaignSummary(id),
            staleTime: 0
          })
        )
      );

      const insightErrors = data?.insights?.errors;
      if (Array.isArray(insightErrors) && insightErrors.length) {
        toast.error(`Synced with ${insightErrors.length} insight error(s)`);
      } else {
        toast.success(`Synced ${data?.campaigns_upserted ?? list.length} campaigns`);
      }
      refetchCampaigns();
    },
    onError: (err) => {
      const msg =
        err?.response?.status === 403
          ? 'Facebook permission missing — reconnect with ads access'
          : err?.response?.data?.message || err?.message || 'Sync failed';
      toast.error(msg);
    }
  });

  const handleCompanyChange = (value) => {
    setCompanyId(value);
    if (value) localStorage.setItem('companyId', value);
  };

  const isLoading = !!companyId && (isCampaignsPending || summariesInitialPending);

  return (
    <Layout>
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header
          data={{ name: 'Marketing > Facebook Campaigns' }}
          backArrow={true}
          onBack={onBack}
        />
      </div>

      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="mt-[4.5rem] w-full min-h-screen bg-gray-100 p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">Facebook Campaigns</h1>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {companies.length > 0 ? (
                <select
                  className="text-sm border border-stone-200 rounded-full px-3 py-1.5 bg-white text-gray-700"
                  value={companyId}
                  onChange={(e) => handleCompanyChange(e.target.value)}>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.company_name || c.name || `Company #${c.id}`}
                    </option>
                  ))}
                </select>
              ) : null}
              <select
                className="text-sm border border-stone-200 rounded-full px-3 py-1.5 bg-white text-gray-700"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status">
                <option value="all">All statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="PAUSED">Paused</option>
              </select>
              <span className="text-xs font-medium text-gray-500 bg-white border border-stone-200 rounded-full px-3 py-1.5">
                Last synced: {lastSynced}
              </span>
              <Button
                displayType="secondary"
                onClick={() => syncNow()}
                disabled={!companyId || isSyncing}>
                <span className="flex items-center gap-2">
                  {isSyncing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  {isSyncing ? 'Syncing…' : 'Sync now'}
                </span>
              </Button>
            </div>
          </div>

          {!companyId ? (
            <div className="rounded-xl border border-dashed border-stone-300 bg-white px-6 py-12 text-center">
              <p className="text-sm text-gray-600">Select a company to view Meta campaigns.</p>
            </div>
          ) : isCampaignsError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-6 py-8 text-center">
              <p className="text-sm text-rose-700">
                {campaignsError?.response?.data?.message ||
                  campaignsError?.message ||
                  'Failed to load campaigns'}
              </p>
              <div className="mt-4 flex justify-center gap-2">
                <Button displayType="secondary" onClick={() => refetchCampaigns()}>
                  Retry
                </Button>
                <Button
                  displayType="secondary"
                  onClick={() => navigate('/builder/integration/facebook-companies')}>
                  Set up campaigns
                </Button>
              </div>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-16">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading campaign metrics…
            </div>
          ) : campaigns.length === 0 ? (
            <div className="rounded-xl border border-dashed border-stone-300 bg-white px-6 py-12 text-center">
              <p className="text-sm text-gray-600 mb-4">
                No Meta campaigns yet. Sync from Ads Manager or finish campaign setup.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <Button onClick={() => syncNow()} disabled={isSyncing}>
                  {isSyncing ? 'Syncing…' : 'Sync now'}
                </Button>
                <Button
                  displayType="secondary"
                  onClick={() => navigate('/builder/integration/facebook-companies')}>
                  Set up campaigns
                </Button>
              </div>
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="rounded-xl border border-dashed border-stone-300 bg-white px-6 py-12 text-center">
              <p className="text-sm text-gray-600 mb-4">
                No {statusFilter === 'ACTIVE' ? 'active' : 'paused'} campaigns for this company.
              </p>
              <Button displayType="secondary" onClick={() => setStatusFilter('all')}>
                Show all
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch">
              {filteredCampaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  onLinkProject={() => navigate('/builder/integration/facebook-companies')}
                  onViewBreakdown={() =>
                    setBreakdownCampaign({ id: campaign.id, name: campaign.name })
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {breakdownCampaign ? (
        <CampaignBreakdownModal
          campaignId={breakdownCampaign.id}
          campaignName={breakdownCampaign.name}
          onClose={() => setBreakdownCampaign(null)}
        />
      ) : null}
    </Layout>
  );
};

export default FacebookCampaignsDashboard;
